import { Book, MenuBook, PhoneAndroid } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Typography,
  useColorScheme,
} from "@mui/joy";
import { useMediaQuery, useTheme } from "@mui/material";
import ReactPlayer from "react-player";
import { BookMode } from "../App";
import AboutUs from "./AboutUs";
import HatchlingPreludeSection from "./HatchlingPreludeSection";
import InteractiveMap from "./InteractiveMap";
import PreludeSection from "./PreludeSection";
import SapphireShorePOVs from "./SapphireShorePOVs";
import SapphireShoreRealms from "./SapphireShoreRealms";

interface BookContentProps {
  selectedBook: BookMode;
}

const bookData = {
  "sapphire-shore": {
    title: "The Sapphire Shore",
    subtitle: "Book 1 - Available Now",
    description:
      "We bid you welcome, to the first volume telling the tale of Osgara. Where the young and newly promoted Battlemaster Lysiac seeks to prove his worth to his Empire. Where the Sapphire Lady of War Mithra waits patiently in her citadel city. Where a young village girl stumbles onto a trail of magic.",
    videoUrl: "https://youtu.be/Zti-1PIOts8",
    purchaseLinks: [
      {
        format: "Kindle",
        link: "https://amzn.eu/d/bsaY5Ib",
      },
      {
        format: "Paperback",
        link: "https://amzn.eu/d/8gSWULc",
      },
      {
        format: "Hardcover",
        link: "https://amzn.eu/d/8MRD6pj",
      },
    ],
    novellas: [
      {
        title: "Reports of Rebellion",
        status: "Free",
        link: "#reportsOfRebellion",
      },
      {
        title: "Heart of Gold",
        status: "Available",
        link: "https://amzn.eu/d/5JLLdjT",
      },
      {
        title: "Scoundrels!",
        status: "Available",
        link: "https://amzn.eu/d/5EPxf4G",
      },
      {
        title: "Juniara: Last of the Free",
        status: "Available",
        link: "https://amzn.eu/d/08cUpp8h",
      },
    ],
    color: "rgba(59, 130, 246, 0.8)",
  },
  "ember-tide": {
    title: "The Ember Tide",
    subtitle: "Book 2 - Available Now",
    description:
      "Fifteen long years have passed since the shattering, since the birth of magic, since 'she' died and left behind a fractured world. Or so they all believed. Fifteen years of decay and collapse for some realms, fifteen years of discovery and enlightenment for others, and fifteen years of war as the world remains blind to Mithra's fate. Fifteen years past the advent of magic, and a dragon's roar could be heard in the south. She will come to them, and magic will be broken, or they will burn.",
    videoUrl: null,
    purchaseLinks: [
      {
        format: "Kindle",
        link: "https://amzn.eu/d/4hw66xI",
      },
      {
        format: "Paperback",
        link: "https://amzn.eu/d/0Q2pD1g",
      },
      {
        format: "Hardcover",
        link: "https://amzn.eu/d/33ZHkey",
      },
    ],
    novellas: [
      {
        title: "Hatchling of the Orchard",
        status: "Free",
        link: "#hatchlingOfTheOrchard",
      },
    ],
    color: "rgba(239, 68, 68, 0.8)",
  },
  "third-book": {
    title: "???",
    subtitle: "Book 3 - Coming 2027",
    description: "???",
    videoUrl: null,
    purchaseLinks: [],
    novellas: [
      { title: "???", status: "Coming Soon", link: "#" },
      { title: "???", status: "Coming Soon", link: "#" },
      { title: "???", status: "Coming Soon", link: "#" },
    ],
    color: "rgba(168, 85, 247, 0.8)",
  },
};

export default function BookContent({ selectedBook }: BookContentProps) {
  const theme = useTheme();
  const { mode } = useColorScheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = mode === "dark";
  const book = bookData[selectedBook];

  return (
    <Box sx={{ py: 4 }}>
      {/* Book Header */}
      <Card
        variant="outlined"
        sx={{
          mb: 6,
          background: isDark
            ? `linear-gradient(135deg, ${book.color}20 0%, rgba(0, 0, 0, 0.6) 100%)`
            : `linear-gradient(135deg, ${book.color}15 0%, rgba(255, 255, 255, 0.8) 100%)`,
          border: `1px solid ${book.color}40`,
          backdropFilter: "blur(10px)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 30% 70%, ${book.color}10 0%, transparent 50%)`,
            zIndex: -1,
          }}
        />

        <Box sx={{ p: 4 }}>
          <Typography
            level="h2"
            sx={{
              color: book.color,
              textAlign: "center",
              mb: 1,
              letterSpacing: "0.05em",
            }}
          >
            {book.title}
          </Typography>

          <Typography
            level="body-lg"
            sx={{
              color: isDark
                ? "rgba(255, 255, 255, 0.7)"
                : "rgba(30, 41, 59, 0.7)",
              textAlign: "center",
              mb: 3,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              fontSize: "0.9rem",
            }}
          >
            {book.subtitle}
          </Typography>

          <Typography
            level="body-md"
            sx={{
              color: isDark
                ? "rgba(255, 255, 255, 0.8)"
                : "rgba(30, 41, 59, 0.8)",
              textAlign: "center",
              lineHeight: 1.7,
              maxWidth: "800px",
              margin: "0 auto",
              mb: 4,
            }}
          >
            {book.description}
          </Typography>

          {/* Video Player for Sapphire Shore */}
          {book.videoUrl && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 4,
              }}
            >
              <ReactPlayer
                url={book.videoUrl}
                controls
                width={isMobile ? "100%" : "600px"}
                height={isMobile ? "200px" : "340px"}
                style={{
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              />
            </Box>
          )}

          {/* Purchase Links */}
          {book.purchaseLinks && book.purchaseLinks.length > 0 && (
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                level="title-md"
                sx={{
                  color: isDark ? "white" : "#1e293b",
                  mb: 3,
                  letterSpacing: "0.05em",
                }}
              >
                Get Your Copy
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  justifyContent: "center",
                }}
              >
                {book.purchaseLinks.map((purchase, index) => (
                  <Button
                    key={index}
                    component="a"
                    href={purchase.link}
                    target="_blank"
                    size="lg"
                    variant="solid"
                    startDecorator={
                      purchase.format === "Kindle" ? (
                        <PhoneAndroid />
                      ) : purchase.format === "Paperback" ? (
                        <MenuBook />
                      ) : (
                        <Book />
                      )
                    }
                    sx={{
                      background: `linear-gradient(45deg, ${book.color}, ${book.color}cc) !important`,
                      backgroundColor: `${book.color} !important`,
                      color: "white !important",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontFamily: "'Cinzel', serif",
                      px: 4,
                      py: 1.5,
                      fontSize: "0.9rem",
                      boxShadow: `0 4px 15px ${book.color}40`,
                      "&:hover": {
                        background: `linear-gradient(45deg, ${book.color}cc, ${book.color}) !important`,
                        backgroundColor: `${book.color} !important`,
                        boxShadow: `0 6px 20px ${book.color}60`,
                        transform: "translateY(-2px)",
                      },
                      "&:active": {
                        background: `linear-gradient(45deg, ${book.color}dd, ${book.color}) !important`,
                        backgroundColor: `${book.color} !important`,
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {purchase.format}
                  </Button>
                ))}
              </Box>
            </Box>
          )}

          {/* Novellas */}
          <Box sx={{ textAlign: "center" }}>
            <Typography
              level="title-md"
              sx={{
                color: isDark ? "white" : "#1e293b",
                mb: 3,
                letterSpacing: "0.05em",
              }}
            >
              Related Stories
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                justifyContent: "center",
              }}
            >
              {book.novellas.map((novella, index) => (
                <Chip
                  key={index}
                  variant={novella.status === "Free" ? "solid" : "outlined"}
                  component="a"
                  href={novella.link}
                  target={novella.link.startsWith("http") ? "_blank" : "_self"}
                  sx={{
                    background:
                      novella.status === "Free"
                        ? `linear-gradient(45deg, ${book.color}, ${book.color}cc) !important`
                        : "transparent !important",
                    backgroundColor:
                      novella.status === "Free"
                        ? `${book.color} !important`
                        : "transparent !important",
                    border: `1px solid ${book.color}`,
                    color: novella.status === "Free" ? "white" : book.color,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    fontSize: "0.75rem",
                    fontFamily: "'Cinzel', serif",
                    cursor: novella.link === "#" ? "not-allowed" : "pointer",
                    opacity: novella.link === "#" ? 0.5 : 1,
                    "&:hover": {
                      background:
                        novella.link !== "#"
                          ? `linear-gradient(45deg, ${book.color}, ${book.color}cc) !important`
                          : undefined,
                      backgroundColor:
                        novella.link !== "#"
                          ? `${book.color} !important`
                          : undefined,
                      color: novella.link !== "#" ? "white" : undefined,
                    },
                  }}
                >
                  {novella.title}
                  {novella.status === "Free" && " (Free)"}
                </Chip>
              ))}
            </Box>
          </Box>
        </Box>
      </Card>

      {/* Book-specific content */}
      {selectedBook === "sapphire-shore" && (
        <>
          <Divider
            sx={{
              my: 6,
              borderColor: isDark
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)",
            }}
          />
          <InteractiveMap />
          <Divider
            sx={{
              my: 6,
              borderColor: isDark
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)",
            }}
          />
          <SapphireShoreRealms />
          <Divider
            sx={{
              my: 6,
              borderColor: isDark
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)",
            }}
          />
          <SapphireShorePOVs />
          <Divider
            sx={{
              my: 6,
              borderColor: isDark
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)",
            }}
          />
          <PreludeSection />
          <Divider
            sx={{
              my: 6,
              borderColor: isDark
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)",
            }}
          />
          <AboutUs accentColor={book.color} />
        </>
      )}

      {/* Book 2 - The Ember Tide content */}
      {selectedBook === "ember-tide" && (
        <>
          <Divider
            sx={{
              my: 6,
              borderColor: isDark
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)",
            }}
          />
          <HatchlingPreludeSection />
          <Divider
            sx={{
              my: 6,
              borderColor: isDark
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)",
            }}
          />
          <AboutUs accentColor={book.color} />
        </>
      )}
    </Box>
  );
}
